import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiDollarSign,
  FiShoppingBag,
  FiArrowLeft,
  FiPhone,
  FiMapPin,
  FiMail,
  FiCreditCard,
  FiFileText,
  FiChevronDown,
  FiChevronUp,
  FiCalendar,
  FiTrash2
} from 'react-icons/fi';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import './retailerDetails.css';

const RetailerDetailsPage = () => {
  const { retailerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({
    retailer: null,
    payments: [],
    sales: [],
    loading: true,
    error: null
  });
  const [expandedSales, setExpandedSales] = useState({});
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);

const handleDownloadStatement = async () => {
  if (!dateRange.startDate || !dateRange.endDate) {
    alert('Please select a start and end date first.');
    return;
  }
  if (!data.retailer) {
    alert('Retailer data not loaded yet.');
    return;
  }

  setDownloadingPdf(true);
  try {
    const response = await axios.get(
      `/retailers/${retailerId}/statement/pdf`,
      {
        params: { start: dateRange.startDate, end: dateRange.endDate },
        responseType: 'blob'
      }
    );

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `statement-${data.retailer.name.replace(/\s+/g, '_')}-${dateRange.startDate}-to-${dateRange.endDate}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error downloading statement:', err);
    alert('Failed to generate statement PDF.');
  } finally {
    setDownloadingPdf(false);
  }
};

  const fetchData = async () => {
    try {
      setData(prev => ({...prev, loading: true}));
      const response = await axios.get(
        `/retailers/details/${retailerId}`
      );

      setData({
        retailer: response.data.retailer,
        payments: response.data.payments || [],
        sales: response.data.sales || [],
        loading: false,
        error: null
      });
    } catch (err) {
      setData({
        retailer: null,
        payments: [],
        sales: [],
        loading: false,
        error: err.response?.data?.error || 'Failed to load data'
      });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailerId]);

  const handleDeletePaymentClick = (payment) => {
    setPaymentToDelete(payment);
  };

  const handleConfirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      setDeletingPayment(true);
      await axios.delete(`/retailers/${retailerId}/payments/${paymentToDelete.payment_id}`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting payment:', err);
      alert(err.response?.data?.error || 'Failed to delete payment.');
    } finally {
      setDeletingPayment(false);
      setPaymentToDelete(null);
    }
  };

  const handleCancelDeletePayment = () => {
    setPaymentToDelete(null);
  };

  const toggleSaleDetails = (saleDate) => {
    setExpandedSales(prev => ({
      ...prev,
      [saleDate]: !prev[saleDate]
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilter = (e) => {
    e.preventDefault();
    // Filtering is done in the rendering logic below
  };

  const handleClearFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
  };

  if (data.loading) return <div className="loading">Loading retailer details...</div>;
  if (data.error) return <div className="error">Error: {data.error}</div>;
  if (!data.retailer) return <div className="error">Retailer not found</div>;

  // Destructure retailer with default values
  const { retailer, payments, sales } = data;
  const { due_amount = 0 } = retailer;

  // Filter sales and payments based on date range
  const filteredSales = dateRange.startDate && dateRange.endDate 
    ? sales.filter(sale => {
        const saleDate = new Date(sale.sale_date);
        return saleDate >= new Date(dateRange.startDate) && 
               saleDate <= new Date(dateRange.endDate);
      })
    : sales;

  const filteredPayments = dateRange.startDate && dateRange.endDate 
    ? payments.filter(payment => {
        const paymentDate = new Date(payment.payment_date);
        return paymentDate >= new Date(dateRange.startDate) && 
               paymentDate <= new Date(dateRange.endDate);
      })
    : payments;

  // Group sales by invoice (using filtered sales)
  const salesByInvoice = filteredSales.reduce((acc, sale) => {
    const invId = sale.invoice_no || sale.invoice_id; // fallback to id if no missing
    if (!acc[invId]) {
      acc[invId] = {
        invoice_id: sale.invoice_id,
        invoice_no: sale.invoice_no,
        date: new Date(sale.sale_date).toLocaleDateString('en-IN'),
        raw_date: sale.sale_date,
        status: sale.status || 'pending',
        cleared_at: sale.cleared_at,
        total: parseFloat(sale.invoice_total_amount) || 0,
        paid: parseFloat(sale.paid_amount) || 0,
        items: []
      };
    }
    acc[invId].items.push(sale);
    return acc;
  }, {});

  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const saleSummaries = Object.values(salesByInvoice).map((inv, index) => {
    const due = inv.total - inv.paid;
    const days = calculateDays(inv.raw_date, inv.cleared_at);
    return {
      sn: index + 1,
      ...inv,
      due,
      days
    };
  }).sort((a, b) => new Date(b.raw_date) - new Date(a.raw_date));

  // Calculate financial summaries (always use full data, not filtered)
  const allSalesByInvoice = sales.reduce((acc, sale) => {
    const invId = sale.invoice_id;
    if (!acc[invId]) {
      acc[invId] = parseFloat(sale.invoice_total_amount) || 0;
    }
    return acc;
  }, {});

  const totalSalesValue = Object.values(allSalesByInvoice).reduce((sum, total) => sum + total, 0);

  const totalPayments = payments.reduce(
    (sum, payment) => sum + (parseFloat(payment.amount) || 0), 0
  );

  const totalDue = due_amount;

  return (
    <div className="retailer-details-container">
      <div className="header">
        <button onClick={() => navigate('/retailers')} className="back-button">
          <FiArrowLeft /> Back to Retailers
        </button>
        <h1>{retailer.name}</h1>
        <div className="retailer-info">
          <p><FiPhone /> {retailer.contact_number}</p>
          {retailer.email && <p><FiMail /> {retailer.email}</p>}
          {retailer.address && <p><FiMapPin /> {retailer.address}</p>}
        </div>
      </div>

      <div className="financial-summary">
        <div className="summary-card">
          <h3><FiShoppingBag /> Total Sales Value</h3>
          <p>Rs {totalSalesValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="summary-card">
          <h3><FiDollarSign /> Total Payments</h3>
          <p>Rs {totalPayments.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className={`summary-card ${totalDue > 0 ? 'due' : 'paid'}`}>
          <h3>Balance Due</h3>
          <p>
            Rs {Math.abs(totalDue).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            {totalDue < 0 && ' (Credit Balance)'}
          </p>
        </div>
      </div>

    <div className="date-filter-compact">
  <form onSubmit={handleFilter}>
    <h3><FiCalendar /> Select date:</h3>
    <input type="date" name="startDate" value={dateRange.startDate} onChange={handleDateChange} placeholder="From" />
    <input type="date" name="endDate" value={dateRange.endDate} onChange={handleDateChange} min={dateRange.startDate} placeholder="To" />
    <button type="submit" title="Apply Filter">🔍</button>
    <button type="button" onClick={handleClearFilter} title="Clear Filter">❌</button>
    <button
      type="button"
      onClick={handleDownloadStatement}
      disabled={downloadingPdf}
      className="download-statement-btn"
      title="Download PDF Statement"
    >
      {downloadingPdf ? 'Generating...' : '📄 Download Statement'}
    </button>
  </form>
</div>

      <div className="details-section">
    

        <div className="sales-section">
          <h2>Sales History</h2>
          {saleSummaries.length > 0 ? (
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Total Amount</th>
                  <th>Paid Amount</th>
                  <th>Due Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {saleSummaries.map((saleSummary) => (
                  <React.Fragment key={saleSummary.invoice_id}>
                    <tr>
                      <td>{saleSummary.invoice_no || `Inv #${saleSummary.invoice_id}`}</td>
                      <td>{saleSummary.date}</td>
                      <td>
                        {saleSummary.status === 'paid' ? (
                          <span className="badge paid">Cleared in {saleSummary.days} days</span>
                        ) : saleSummary.status === 'partial' ? (
                          <span className="badge partial">partial (Pending {saleSummary.days} days)</span>
                        ) : (
                          <span className="badge pending">Pending {saleSummary.days} days</span>
                        )}
                      </td>
                      <td>Rs {saleSummary.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>Rs {saleSummary.paid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>Rs {saleSummary.due.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <button 
                          onClick={() => toggleSaleDetails(saleSummary.invoice_id)}
                          className="view-details-btn"
                        >
                          {expandedSales[saleSummary.invoice_id] ? (
                            <>
                              <FiChevronUp /> Hide Details
                            </>
                          ) : (
                            <>
                              <FiChevronDown /> View Details
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedSales[saleSummary.invoice_id] && (
                      <tr className="sale-details-row">
                        <td colSpan="7">
                          <table className="items-details-table">
                            <thead>
                              <tr>
                                <th>Item Name</th>
                                <th>Quantity</th>
                                <th>Unit Price</th>
                                <th>Total Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {saleSummary.items.map(sale => (
                                <tr key={sale.sale_id}>
                                  <td>{sale.item_name || 'Unknown Item'}</td>
                                  <td>{sale.quantity} {sale.unit}</td>
                                  <td>Rs {sale.selling_price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                  <td>Rs {sale.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              {filteredSales.length === 0 && sales.length > 0 
                ? 'No sales found for selected date range' 
                : 'No sales recorded for this retailer'}
            </div>
          )}
        </div>

        <div className="payments-section">
          <h2>Payment History</h2>
          {filteredPayments.length > 0 ? (
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Receipt No</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment, index) => (
                  <tr key={payment.payment_id}>
                    <td>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</td>
                    <td>Rs {payment.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td>
                      <span className={`payment-type ${payment.payment_type}`}>
                        <FiCreditCard className="payment-icon" />
                        {payment.payment_type || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="receipt-info">
                        <FiFileText className="receipt-icon" />
                        {payment.receipt_no || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="delete-btn"
                        title="Delete payment"
                        onClick={() => handleDeletePaymentClick(payment)}
                        disabled={deletingPayment}
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              {filteredPayments.length === 0 && payments.length > 0 
                ? 'No payments found for selected date range' 
                : 'No payments recorded'}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        show={!!paymentToDelete}
        title="Delete Payment"
        message={
          paymentToDelete
            ? `Are you sure you want to delete this Rs ${paymentToDelete.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} payment from ${new Date(paymentToDelete.payment_date).toLocaleDateString('en-IN')}? This will add the amount back to the retailer's due balance.`
            : ''
        }
        onConfirm={handleConfirmDeletePayment}
        onCancel={handleCancelDeletePayment}
      />
    </div>
  );
};

export default RetailerDetailsPage;